import React from 'react';
import { 
  Briefcase, Star, Target, ShieldCheck, Award, 
  GraduationCap, Compass, BookOpen, Layers, PieChart as PieChartIcon, Rocket 
} from 'lucide-react';
import { ResponsiveContainer, PieChart, Pie, Tooltip, Cell } from 'recharts';

const CAREER_GROUPS = {
  'A': { name: "Doğa ve Tarım", color: "#10b981", desc: "Ziraat, Orman, Çevre vb." },
  'B': { name: "Mekanik ve Teknoloji", color: "#f59e0b", desc: "Mimarlık, Makine, Tasarım vb." },
  'C': { name: "Liderlik ve Yönetim", color: "#3b82f6", desc: "Hukuk, İşletme, Yöneticilik vb." },
  'D': { name: "Sanat ve Medya", color: "#ec4899", desc: "Sinema, Müzik, Resim, Medya vb." },
  'E': { name: "Edebiyat ve İletişim", color: "#8b5cf6", desc: "Edebiyat, Gazetecilik, PDR vb." },
  'F': { name: "Sosyal Bilimler", color: "#14b8a6", desc: "Psikoloji, Sosyoloji, Tarih vb." },
  'G': { name: "Uluslararası İlişkiler", color: "#06b6d4", desc: "Uluslararası İlişkiler, Diplomasi vb." },
  'H': { name: "Tıp ve Sağlık", color: "#ef4444", desc: "Tıp, Diş Hekimliği, Eczacılık vb." },
  'I': { name: "Finans ve Analiz", color: "#6366f1", desc: "İktisat, Ekonomi, Finans vb." },
  'İ': { name: "Yazılım ve Bilişim", color: "#84cc16", desc: "Bilgisayar, Yazılım, Bilişim vb." }
};

export const MeslekiEgilimReportUI = ({ data, meta }) => {
  // Veritabanından gelen detaylı puanları alıyoruz, yoksa boş dizi kabul ediyoruz
  const scores = data?.detailedScores || [];

  // Puanları büyükten küçüğe sıralayıp grafik formatına hazırlıyoruz
  const chartData = scores
    .filter(s => CAREER_GROUPS[s.groupKey])
    .map(s => ({
      name: CAREER_GROUPS[s.groupKey].name,
      score: s.score,
      fill: CAREER_GROUPS[s.groupKey].color,
      desc: CAREER_GROUPS[s.groupKey].desc
    }))
    .sort((a, b) => b.score - a.score);

  // Zirvedeki ilk 3 mesleki alanı seçiyoruz
  const top1 = chartData[0] || { name: "Belirsiz", score: 0, fill: "#cbd5e1", desc: "-" };
  const top2 = chartData[1] || { name: "Belirsiz", score: 0, fill: "#cbd5e1", desc: "-" };
  const top3 = chartData[2] || { name: "Belirsiz", score: 0, fill: "#cbd5e1", desc: "-" };

  // 160 soruluk testte 101 barajını geçen alanların sayısını buluyoruz
  const passedThreshold = chartData.filter(s => s.score >= 101);

  return (
    // A4 zırhı: dikey boşluklar ve arka plan sızıntı koruması devrede
    <div className="space-y-5 print:space-y-4 font-sans flex flex-col bg-white relative z-10 print:m-0 print:p-0">
      
      {/* ÜST BAŞLIK */}
      <div className="bg-slate-900 text-white p-4 print:p-3 rounded-2xl flex justify-between items-center shadow-sm shrink-0">
        <div className="flex items-center gap-4 print:gap-3">
          <div className="h-12 w-12 rounded-xl flex items-center justify-center shadow-inner bg-teal-600">
            <Briefcase className="h-6 w-6 text-white"/>
          </div>
          <div>
            <h2 className="text-xl print:text-lg font-black leading-none mb-1">Mesleki Eğilim Detaylı Profili</h2>
            <span className="text-[10px] print:text-[9px] text-slate-400 font-bold tracking-widest uppercase block">
               {meta?.studentClass || "Sınıf Belirtilmemiş"} • NO: {meta?.studentNo || "-"} • {meta?.studentName || "İsimsiz Kayıt"}
            </span>
          </div>
        </div>
        <div className="bg-white/10 px-4 py-2 rounded-xl text-center border border-white/10">
          <span className="text-xl print:text-base font-black block text-teal-400 leading-none">{passedThreshold.length}</span>
          <span className="text-[8px] block opacity-70 uppercase font-bold mt-0.5 tracking-wider">Belirgin Alan</span>
        </div>
      </div>

      {/* ÜST PANEL: DONUT GRAFİK VE LİSTE */}
      <div className="grid grid-cols-1 md:grid-cols-2 print:grid-cols-2 gap-5 print:gap-4 shrink-0 avoid-break">
        
        {/* SOL: PASTA GRAPH BOX */}
        <div className="bg-white p-5 print:p-4 rounded-2xl border border-slate-200 shadow-sm flex flex-col h-[320px] print:h-[290px] overflow-hidden">
          <h3 className="font-black text-slate-800 text-[11px] print:text-[10px] uppercase tracking-widest mb-3 flex items-center border-b pb-2 shrink-0">
            <PieChartIcon className="h-4 w-4 mr-2 text-teal-600"/> 10 Boyutlu Eğilim Dağılımı
          </h3>
          
          <div className="flex-1 flex items-center justify-between min-w-0 gap-3 mt-1">
            <div className="w-[50%] h-full relative flex items-center justify-center">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    innerRadius="60%"
                    outerRadius="90%"
                    paddingAngle={3}
                    dataKey="score"
                    stroke="none"
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', fontSize: '11px' }} 
                    itemStyle={{ color: '#334155', fontWeight: 'bold' }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">
  ÖNE ÇIKAN
</span>
                <span className="text-[11px] font-black text-slate-800 text-center px-2 leading-none mt-1">{top1.name}</span>
              </div>
            </div>

            {/* LEJANT LİSTESİ - TAM VE NET HİZALAMA */}
            <div className="w-[50%] flex flex-col gap-2 print:gap-1.5 justify-start pt-1">
              {chartData.slice(0, 5).map((item, idx) => (
                <div key={idx} className="flex items-center gap-2.5 bg-slate-50 p-2 print:p-1.5 rounded-lg border border-slate-100">
                  <div className="h-3 w-3 rounded-full shrink-0" style={{ backgroundColor: item.fill }}></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] print:text-[9.5px] font-bold text-slate-700 truncate leading-none mb-0.5">{item.name}</p>
                    <p className="text-[9px] print:text-[7.5px] text-slate-400 leading-none">{item.score} Puan</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* SAĞ: EN YÜKSEK 3 ALAN DETAYI */}
        <div className="flex flex-col gap-4 print:gap-3">
          <div className="bg-teal-50/40 p-5 print:p-4 rounded-2xl border border-teal-100 shadow-sm flex-1 flex flex-col justify-center relative overflow-hidden">
            <div className="absolute top-0 right-0 w-20 h-20 opacity-10 rounded-bl-full bg-teal-600"></div>
            <span className="text-[10px] print:text-[9px] font-black uppercase tracking-widest text-teal-600 mb-1.5 flex items-center">
              <Award className="h-4 w-4 mr-1.5"/> En Çok Öne Çıkan Mesleki Alan
            </span>
            <h3 className="text-2xl print:text-xl font-black leading-tight text-slate-800 mb-1">{top1.name}</h3>
            <p className="text-[11px] print:text-[10px] text-slate-500 font-medium leading-relaxed">{top1.desc}</p>
          </div>

          <div className="grid grid-cols-2 gap-4 print:gap-3">
            <div className="bg-slate-50 p-4 print:p-3 rounded-2xl border border-slate-200 shadow-sm flex flex-col justify-center">
                <span className="text-[9px] print:text-[8px] font-black uppercase tracking-widest text-slate-400 mb-1.5 block">2. Öne Çıkan Alan</span>
                <h4 className="text-sm print:text-xs font-black text-slate-700 leading-tight truncate">{top2.name}</h4>
            </div>
            <div className="bg-slate-50 p-4 print:p-3 rounded-2xl border border-slate-200 shadow-sm flex flex-col justify-center">
                <span className="text-[9px] print:text-[8px] font-black uppercase tracking-widest text-slate-400 mb-1.5 block">3. Öne Çıkan Alan</span>
                <h4 className="text-sm print:text-xs font-black text-slate-700 leading-tight truncate">{top3.name}</h4>
            </div>
          </div>
          
          <div className="bg-amber-50/60 p-4 print:p-3 rounded-2xl border border-amber-100 flex items-center gap-3">
             <Target className="h-8 w-8 text-amber-500 shrink-0"/>
            <p className="text-[10px] print:text-[8.5px] text-amber-800 font-medium leading-tight">
  Öğrencinin yanıtlarında <strong>{top1.name}</strong> alanına ilişkin eğilim diğer mesleki alanlara göre daha fazla öne çıkmaktadır. Bu sonuç, kariyer görüşmesinde ayrıntılı olarak ele alınabilecek bir keşif alanıdır.
</p>
          </div>
        </div>
      </div>

      {/* ALT PANEL: UZMAN GÖRÜŞ VE YÖNLENDİRME PENCERELERİ */}
      <div className="grid grid-cols-1 md:grid-cols-2 print:grid-cols-2 gap-5 print:gap-4 shrink-0 avoid-break">
        
        {/* KART 1: MÜLAKAT NOTLARI */}
        <div className="bg-white p-5 print:p-4 rounded-2xl border border-slate-200 shadow-sm flex flex-col h-[140px] print:h-[130px] overflow-hidden">
          <div className="flex items-center gap-3 mb-2.5">
            <div className="h-8 w-8 rounded-lg flex items-center justify-center bg-teal-50">
              <Compass className="h-4 w-4 text-teal-600"/>
            </div>
            <h4 className="font-bold text-slate-800 text-[11px] print:text-[10px] uppercase tracking-wider">Profilin Genel Görünümü</h4>
          </div>
          <p className="text-[11px] print:text-[10px] text-slate-600 leading-relaxed text-justify line-clamp-3">
            İlk sıralarda yer alan alanlar, öğrencinin bu envanterde daha sık tercih ettiği mesleki yönelim kümelerini göstermektedir. Profil; öğrenci görüşmesi, diğer kariyer envanterleri ve akademik verilerle birlikte değerlendirilmelidir.
          </p>
        </div>

        {/* KART 2: AKADEMİK POTANSİYEL */}
        <div className="bg-white p-5 print:p-4 rounded-2xl border border-slate-200 shadow-sm flex flex-col h-[140px] print:h-[130px] overflow-hidden">
          <div className="flex items-center gap-3 mb-2.5">
            <div className="h-8 w-8 rounded-lg flex items-center justify-center bg-blue-50">
              <GraduationCap className="h-4 w-4 text-blue-600"/>
            </div>
            <h4 className="font-bold text-slate-800 text-[11px] print:text-[10px] uppercase tracking-wider">Akademik Verilerle Birlikte Değerlendirme</h4>
          </div>
          <p className="text-[11px] print:text-[10px] text-slate-600 leading-relaxed text-justify line-clamp-3">
            Bu envanter akademik başarı veya yetenek düzeyini ölçmez. Öne çıkan mesleki alanların öğrencinin ders performansı, çalışma alışkanlıkları ve eğitim hedefleriyle birlikte ele alınması önerilir.
          </p>
        </div>

        {/* KART 3: GÖRÜŞME ODAKLARI */}
        <div className="bg-white p-5 print:p-4 rounded-2xl border border-slate-200 shadow-sm flex flex-col h-[140px] print:h-[130px] overflow-hidden">
          <div className="flex items-center gap-3 mb-2.5">
            <div className="h-8 w-8 rounded-lg flex items-center justify-center bg-purple-50">
              <BookOpen className="h-4 w-4 text-purple-600"/>
            </div>
            <h4 className="font-bold text-slate-800 text-[11px] print:text-[10px] uppercase tracking-wider">Danışmanlık Odak Noktaları</h4>
          </div>
          <p className="text-[11px] print:text-[10px] text-slate-600 leading-relaxed text-justify line-clamp-3">
            Öğrenciyle yapılacak bireysel görüşmede, ilk 3 sıradaki alanların iş imkanları, üniversite taban puanları ve sektörün gelecek vizyonu detaylıca ele alınmalıdır.
          </p>
        </div>

        {/* KART 4: AİLE VE ÇEVRE */}
        <div className="bg-white p-5 print:p-4 rounded-2xl border border-slate-200 shadow-sm flex flex-col h-[140px] print:h-[130px] overflow-hidden">
          <div className="flex items-center gap-3 mb-2.5">
            <div className="h-8 w-8 rounded-lg flex items-center justify-center bg-amber-50">
              <Layers className="h-4 w-4 text-amber-600"/>
            </div>
            <h4 className="font-bold text-slate-800 text-[11px] print:text-[10px] uppercase tracking-wider">Çevresel Destek Faktörleri</h4>
          </div>
          <p className="text-[11px] print:text-[10px] text-slate-600 leading-relaxed text-justify line-clamp-3">
            Kariyer planlama sürecinde ailenin beklentileri ile öğrencinin bu testte ortaya koyduğu gerçek eğilimlerin ortak bir paydada buluşturulması kritik önem taşır.
          </p>
        </div>

        {/* GENİŞ KART: UZMANA STRATEJİK PROJEKSİYON */}
        <div className="bg-slate-50 p-5 print:p-4 rounded-2xl border border-slate-200 shadow-sm col-span-1 md:col-span-2 print:col-span-2 flex items-center gap-4 h-[85px] print:h-[75px]">
           <div className="h-10 w-10 bg-teal-100 rounded-full flex items-center justify-center shrink-0">
             <Rocket className="h-5 w-5 text-teal-600"/>
           </div>
           <div>
             <h4 className="font-bold text-slate-800 text-[10px] uppercase tracking-[0.2em] mb-1">Stratejik Yönlendirme Özeti</h4>
             <p className="text-[11px] print:text-[10px] text-slate-700 leading-relaxed">
               Bu profil, öğrencinin mesleki keşif sürecinde üzerinde durulabilecek alanları görünür hale getirir. Nihai yönlendirme; ilgi, değer, yetenek, akademik veriler, yaşam hedefleri ve öğrenciyle yapılan bireysel görüşmeler birlikte değerlendirilerek yapılmalıdır.
             </p>
           </div>
        </div>
      </div>

      {/* ETİK UYARI */}
      <div className="p-3 print:p-2.5 bg-slate-50 rounded-xl border border-slate-200 shrink-0 mt-auto">
        <p className="text-[10px] print:text-[9px] text-slate-500 text-center leading-relaxed">
          <strong>Önemli Not:</strong> Bu detaylı mesleki eğilim raporu, bir rehberlik ve kariyer danışmanlığı arayüzüdür. Nihai meslek ve branş seçimlerinde öğrencinin güncel akademik başarı sıralamaları da dikkate alınmalıdır.
        </p>
      </div>

    </div>
  );
};